/**
 * Check if a form is valid
 * @param $ jQuery
 * @param form Form element
 * @param honey Honeypot value
 */
export const checkValid = ($: any, form: HTMLFormElement, honey: string) => {
  return new Promise((resolve, reject) => {
    if ($(form).parsley().isValid() && !honey.length) {
      resolve('success');
    } else {
      reject(new Error('not valid'));
    }
  });
};

/**
 * Throttle a function
 * @param callback Callback function
 * @param limit How often to call the callback
 */
export function throttle(callback: Function, limit: number) {
  let waiting = false; // Initially, we're not waiting
  return function() {
    // We return a throttled function
    if (!waiting) {
      // If we're not waiting
      callback.apply(this, arguments); // Execute users function
      waiting = true; // Prevent future invocations
      setTimeout(function() {
        // After a period of time
        waiting = false; // And allow future invocations
      }, limit);
    }
  };
}

/**
 * Preload images
 * @param images Array of image paths
 */
export function preloadImages(images: string[]) {
  images.forEach((image) => {
    new Image().src = image;
  });
}

/**
 * Set a cookie.
 * @param name The name of the cookie
 * @param val The value of the cookie
 * @param days How many days to keep the cookie
 */
export function setCookie(name: string, val: any, days = 30) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = '; expires=' + date.toUTCString();

  const stringValue =
    typeof val === 'string' ? val : JSON.stringify(val);

  const value = encodeURIComponent(stringValue);

  document.cookie = `${name}=${value}${expires}; path=/`;
}

/**
 * Get a cookie.
 * @param name The name of the cookie
 * @returns The value of the cookie
 */
export function getCookie(name: string) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1, c.length);
    }
    if (c.indexOf(nameEQ) === 0) {
      const value = c.substring(nameEQ.length, c.length);
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch (e) {
        console.error('Error parsing cookie', e, value);
        return null;
      }
    }
  }
  return null;
}

/**
 * Wait for a number of milliseconds
 * @param ms Number of milliseconds to wait
 * @returns Promise
 */
export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Get all tabbable elements on the page
 * @returns All tabbable elements on the page
 */
export function getPageButtons() {
  return document.querySelectorAll<HTMLElement>(`
    select:not([tabindex="-1"]),
    input:not([tabindex="-1"]),
    textarea:not([tabindex="-1"]),
    button:not([tabindex="-1"]),
    a:not([tabindex="-1"]),
    iframe:not([tabindex="-1"])
  `);
}

let scrollLockCount = 0;

/** Disables page scrolling */
export const disableScrolling = () => {
  scrollLockCount++;
  // console.debug('Disabling page scroll', { scrollLockCount });
  if (scrollLockCount === 1) {
    document.body.classList.add('stop-scrolling');
  }
};

/** Enables page scrolling */
export const enableScrolling = () => {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  // console.debug('Enabling page scroll', { scrollLockCount });
  if (scrollLockCount === 0) {
    document.body.classList.remove('stop-scrolling');
  }
};

/** Force clear all scroll locks (optional utility) */
export const resetScrollLock = () => {
  scrollLockCount = 0;
  document.body.classList.remove('stop-scrolling');
};

/** Enable or disable a form's inputs */
export const disableInputs = (form: HTMLElement, disable: boolean) => {
  const inputs = form.querySelectorAll('input, textarea, select, button:not(.js-form-message-dismiss');
  inputs.forEach((input) => {
    if (disable) {
      input.setAttribute('disabled', 'true');
    } else {
      input.removeAttribute('disabled');
    }
  });
};

/** Set floating form labels after programmatically setting value */
export const activateFloatingLabel = (
  input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
) => {
  const label = input.previousElementSibling;

  if (label && label.classList.contains('form__label--floating')) {
    if (input.value.trim() !== '') {
      label.classList.add('form__label--active');
    } else {
      label.classList.remove('form__label--active');
    }
  }
};

/**
 * Set loading state on the form submit button
 * @param form The form element
 * @param loading Whether or not to show the loading spinner
 */
export const setLoading = (
  form: HTMLFormElement,
  loading: boolean,
  btnClass: string = 'btn--loading'
) => {
  const submit = form.querySelector<HTMLInputElement>('[type="submit"]');
  if (!submit) {
    console.error('No submit button found');
    return;
  }

  submit.classList.toggle(btnClass, loading);
  submit.disabled = loading;
};

/** Read the customer ID from the body data-attribute */
export const getCustomerId = () => {
  const customerIdAttribute = document.body.dataset.cid;

  if (customerIdAttribute) {
    try {
      const parsedId = parseInt(customerIdAttribute, 10);
      return parsedId;
    } catch (error) {
      console.error('Error reading customer ID', error);
    }
  }

  return null;
};

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait = 300
): T {
  let timeout: number | undefined;
  return function(...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = window.setTimeout(() => func(...args), wait);
  } as T;
}

export const getTabbableElements = (container: HTMLElement): HTMLElement[] => {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    )
  ).filter(
    (el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
  );
};

export const hideElement = (el) => {
  el.setAttribute('aria-hidden', 'true');

  // Wait for the transition to complete before removing from layout
  el.addEventListener('transitionend', function handler() {
    el.style.display = 'none';
    el.removeEventListener('transitionend', handler);
  });
};

export const showElement = (el) => {
  el.style.display = ''; // Reset to default (or 'block', 'flex', etc.)

  // Force reflow to trigger transition
  void el.offsetWidth;

  el.removeAttribute('aria-hidden');
};

// Sets input value while also activating floating label
export const setInput = (form: HTMLFormElement, name: string, value: string | undefined) => {
  const input = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(
    `input[name="${name}"], textarea[name="${name}"]`
  );

  if (input) {
    input.value = value ?? '';
    activateFloatingLabel(input);
  }
};

export const setSelect = (form: HTMLFormElement, name: string, value: string | undefined) => {
  const select = form.querySelector<HTMLSelectElement>(`select[name="${name}"]`);
  if (select) {
    select.value = value ?? '';
    activateFloatingLabel(select);
  }
};

/**
 * Generate the archive page title
 * This isn't currently used; I just didn't want to delete it yet...
 */
export const generateArchivePageTitle = (params: {
  offers: string[];
  department?: string;
  query?: string;
  seoSuffix?: boolean;
  html?: boolean;
}) => {
  const { offers, department, query, seoSuffix = false,
    // html = false,
  } = params;
  let title = '';

  if (query) {
    title += `Search Results for "${query}"`;
  } else {
    title += 'Shop';

    // TODO: Can we restrict to selecting a single option?
    // That would make it an algolia "menu" widget instead of a "refinementList" essentially...
    if (offers.length) {
      title += ` ${offers.join(', ')}`;
    }

    if (department) {
      title += ` ${department}`;
    }
  }

  if (seoSuffix) {
    title += ' | Millbank Hardware';
  }

  return title;
};
